import { useSuggestedKeywordsQuery } from "@graphql/generated/graphql";
import { ArrowTrendingUpIcon } from "@heroicons/react/20/solid";
import classNames from "@lib/classNames";
import { errorToastPopup } from "@lib/errorToastPopup";
import { Trans } from "@lingui/macro";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import { ScrollMenu } from "react-horizontal-scrolling-menu";

export enum TrendingKeywordsLayout {
  column,
  carousel,
}

interface ITrendingKeywordsProps {
  keywords?: string[];
  layout?: TrendingKeywordsLayout;
}

const SideBarTrendingKeywords: React.FC<ITrendingKeywordsProps> = ({
  keywords,
  layout = TrendingKeywordsLayout.column,
}) => {
  const {
    query: { search },
    pathname,
    locale,
  } = useRouter();
  const searchKeyword = Array.isArray(search) ? search[0] : search;

  const shouldFetch = !keywords || keywords.length === 0;

  const { data, loading, error, refetch } = useSuggestedKeywordsQuery({
    skip: !shouldFetch,
    variables: { languageCode: locale },
  });

  const trendingKeywords = useMemo(() => {
    const baseKeywords = shouldFetch
      ? data?.suggestedKeywords ?? []
      : keywords ?? [];

    if (pathname.includes("/explore") && searchKeyword) {
      return baseKeywords.filter((keyword) => keyword !== searchKeyword);
    }

    return baseKeywords;
  }, [shouldFetch, data?.suggestedKeywords, keywords, pathname, searchKeyword]);

  useEffect(() => {
    if (shouldFetch) {
      refetch({ languageCode: locale });
    }
  }, [shouldFetch, locale, refetch]);

  useEffect(() => {
    if (error) {
      errorToastPopup(error);
    }
  }, [error]);

  if (layout === TrendingKeywordsLayout.column) {
    return (
      <div className="flex flex-col space-y-6">
        <p className="font-poppins text-lg font-bold text-text-darkest">
          <Trans>Trending now</Trans>
        </p>
        <div className="flex flex-wrap">
          {trendingKeywords.map((keyword) => (
            <TrendingKeywordPill key={keyword} keyword={keyword} />
          ))}
        </div>
      </div>
    );
  } else {
    return (
      <div className="trending-keywords mx-auto mt-4 h-[58px] max-w-7xl px-4">
        {loading && (
          <Trans>
            <p className="py-4 text-center font-sen text-text-lightest">
              Loading...
            </p>
          </Trans>
        )}
        {trendingKeywords && !error && (
          <ScrollMenu
            transitionDuration={500}
            transitionBehavior={"smooth"}
            scrollContainerClassName="my-4 ml-0 lg:ml-2"
          >
            {trendingKeywords.map((keyword) => (
              <TrendingKeywordPill
                key={keyword}
                keyword={keyword}
                layout={layout}
              />
            ))}
          </ScrollMenu>
        )}
      </div>
    );
  }
};

const TrendingKeywordPill: React.FC<{
  keyword: string;
  layout?: TrendingKeywordsLayout;
}> = ({ keyword, layout = TrendingKeywordsLayout.column }) => {
  const parseQuery = (keyword) => {
    return keyword.split(" ").join("+");
  };

  return (
    <Link href={`/explore?search=${parseQuery(keyword)}`} passHref>
      <div
        className={classNames(
          "transitions-all mb-2 flex flex-row items-center space-x-1 whitespace-nowrap rounded-2xl bg-off-white px-4 py-2 duration-300 hover:cursor-pointer hover:bg-light-background hover:text-text-dark active:scale-90",
          layout === TrendingKeywordsLayout.carousel ? "" : "mr-2"
        )}
      >
        <ArrowTrendingUpIcon className="h-6 w-6 text-primary2" />
        <p className="text-darkest text-md text-left font-sen font-normal">
          {keyword}
        </p>
      </div>
    </Link>
  );
};

export default SideBarTrendingKeywords;
